import React, { useContext, useEffect, useState, memo } from 'react';
import axiosInstance from '../../axiosInstance';
import Typography from "@material-ui/core/Typography";
import Goals from "../Goals/Goals";
import AuthContext from "../../authContext";
import Card from '../../components/Card/Card';
import AddIcon from "@material-ui/icons/Add";
import Button from "@material-ui/core/Button";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Input from "@material-ui/core/Input";
import InputAdornment from "@material-ui/core/InputAdornment";
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';

const useStyles = makeStyles({
  project: {
    width: '100%',
    height: 'calc(100% - 112px)'
  },
  container: {
    justifyContent: 'center',
    flexDirection: 'row',
    display: 'flex',
    padding: '20px',
    marginBottom: '56px'
  },
  paper: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    padding: '20px',
    width: '800px',
    height: '100%',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center'
  },
  rowStart: {
    display: 'flex',
    flexDirection: 'row'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  firstRow: {
    paddingTop: '30px'
  },
  secondRow: {
    paddingTop: '20px'
  },
  firstColumn: {
    paddingRight: '500px'
  },
  thirdColumn: {
    paddingTop: '10px',
    paddingRight: '500px'
  },
  fourthColumn: {
    paddingTop: '10px',
  },
  category: {
    minWidth: '126px'
  },
  active: {
    color: 'rgb(55, 71, 172)'
  },
  inactive: {
    color: 'rgb(88, 101, 109)'
  },
  backdrop: {
    zIndex: 99999,
    // Prevent the user from selecting the text.
    userSelect: 'none',
    msUserSelect: 'none',
    msTouchSelect: 'none',
    WebkitUserSelect: 'none',
    KhtmlUserSelect: 'none',
    MozUserSelect: 'none',
    cursor: 'default'
  },
  file: {
    width: '400px',
    paddingTop: '20px'
  },
  innerContainer: {
    justifyContent: 'flex-end'
  },
  textStart: {
    textAlign: 'start'
  },
  vouchersColumn: {
    paddingLeft: '60px'
  }
});

const Project = ({ match }) => {
  const classes = useStyles();
  const [vouchers, setVouchers] = useState([]);
  const { currentUser } = useContext(AuthContext);
  const [project, setProject] = useState(null);

  let isOwner = false;
  if (project !== null && currentUser !== null) {
    isOwner = currentUser.email === project.creator.email;
  }
  let totalProjectGoal = 0;
  if (project !== null) {
    totalProjectGoal = project.goals.reduce((cur, goal) => {
      return goal.goalTarget + cur;
    }, 0);
  }

  useEffect(() => {
    // If a project with id requestedProjectId doesn't exist redirect to / (there isn't enough time for a 404 page.
    const requestedProjectId = match.params.id;

    const normalizeAndSetProject = (responseData) => {
      const normalizedProjects = Object.values(responseData).map((userProjects) => Object.keys(userProjects).map((userProjectId) => {
        return {
          ...userProjects[userProjectId],
          id: userProjectId
        };
      })).flat();

      setProject(normalizedProjects.find((project) => project.id === requestedProjectId));
    };


    const fetchProjects = async () => {
      const response = await axiosInstance('.json');

      normalizeAndSetProject(response.data);
    };

    fetchProjects();
  }, [match.params.id]);

  const onVoucherNameChanged = (event, index) => {
    const newVoucher = {
      name: event.target.value,
      originalPrice: vouchers[index].originalPrice,
      voucherPrice: vouchers[index].voucherPrice,
      additionalPrice: vouchers[index].additionalPrice,
      description: vouchers[index].description
    };

    setVouchers((previousVouchers) => {
      const newVouchers = [...previousVouchers];
      newVouchers[index] = newVoucher;

      return newVouchers;
    });
  };

  const onVoucherOriginalPriceChanged = (event, index) => {
    const newVoucher = {
      name: vouchers[index].name,
      originalPrice: event.target.value,
      voucherPrice: vouchers[index].voucherPrice,
      additionalPrice: vouchers[index].additionalPrice,
      description: vouchers[index].description
    };

    setVouchers((previousVouchers) => {
      const newVouchers = [...previousVouchers];
      newVouchers[index] = newVoucher;

      return newVouchers;
    });
  };

  const onVoucherPriceChanged = (event, index) => {
    const newVoucher = {
      name: vouchers[index].name,
      originalPrice: vouchers[index].originalPrice,
      voucherPrice: event.target.value,
      additionalPrice: vouchers[index].additionalPrice,
      description: vouchers[index].description
    };

    setVouchers((previousVouchers) => {
      const newVouchers = [...previousVouchers];
      newVouchers[index] = newVoucher;

      return newVouchers;
    });
  };

  const onVoucherAdditionalPriceChanged = (event, index) => {
    const newVoucher = {
      name: vouchers[index].name,
      originalPrice: vouchers[index].originalPrice,
      voucherPrice: vouchers[index].voucherPrice,
      additionalPrice: event.target.value,
      description: vouchers[index].description
    };

    setVouchers((previousVouchers) => {
      const newVouchers = [...previousVouchers];
      newVouchers[index] = newVoucher;

      return newVouchers;
    });
  };

  const onVoucherDescriptionChanged = (event, index) => {
    const newVoucher = {
      name: vouchers[index].name,
      originalPrice: vouchers[index].originalPrice,
      voucherPrice: vouchers[index].voucherPrice,
      additionalPrice: vouchers[index].additionalPrice,
      description: event.target.value
    };

    setVouchers((previousVouchers) => {
      const newVouchers = [...previousVouchers];
      newVouchers[index] = newVoucher;

      return newVouchers;
    });
  };

  const generateVoucherElement = (index) => {
    return (
      <div className={classes.voucher} key={index}>
        <TextField
          required
          value={vouchers[index].name}
          onChange={(event) => onVoucherNameChanged(event, index)}
          label="Voucher"
          className={classes.voucherName}
        />
        <FormControl className={classes.voucherPrice}>
          <InputLabel>Original Price</InputLabel>
          <Input
            value={vouchers[index].originalPrice}
            onChange={(event) => onVoucherOriginalPriceChanged(event, index)}
            startAdornment={<InputAdornment position="start">€</InputAdornment>}
          />
        </FormControl>
        <FormControl className={classes.voucherPrice}>
          <InputLabel>Voucher Price</InputLabel>
          <Input
            value={vouchers[index].voucherPrice}
            onChange={(event) => onVoucherPriceChanged(event, index)}
            startAdornment={<InputAdornment position="start">€</InputAdornment>}
          />
        </FormControl>
        <FormControl className={classes.voucherPrice}>
          <InputLabel>Additional Price</InputLabel>
          <Input
            value={vouchers[index].additionalPrice}
            onChange={(event) => onVoucherAdditionalPriceChanged(event, index)}
            startAdornment={<InputAdornment position="start">€</InputAdornment>}
          />
        </FormControl>
        <TextField
          required
          value={vouchers[index].description}
          onChange={(event) => onVoucherDescriptionChanged(event, index)}
          label="Description"
          className={classes.voucherName}
        />
      </div>
    );
  };

  const onAddVoucherButtonClicked = () => {
    setVouchers((previousVouchers) => {
      return [...previousVouchers, {
        name: '',
        originalPrice: 0,
        voucherPrice: 0,
        additionalPrice: 0,
        description: ''
      }];
    });
  };

  let creationDate = '';
  if (project !== null) {
    creationDate = new Date(project.creationDate).toDateString();
  }

  return (
    <>
      {project === null ?
        <Backdrop
          className={classes.backdrop}
          open={true}
        >
          <CircularProgress color="primary" />
        </Backdrop> :
        <div className={classes.container}>
          <Paper className={classes.paper} elevation={3}>
            <div className={classes.projects}>
              <div className={classes.column}>
                <div className={`${classes.row} ${classes.firstRow}`}>
                  <Typography variant="h4"
                    component="p" gutterBottom
                  >
                    Project "{project.name}"
                </Typography>
                </div>
                <Typography variant="overline" gutterBottom color="textSecondary">
                  <b>{project.address}</b>
                </Typography>
                <Typography variant="caption" gutterBottom color="textSecondary">
                  Category: <b>{project.category}</b>
                </Typography>
                <Typography variant="caption" gutterBottom color="textSecondary">
                  Creation date: <b>{creationDate}</b>
                </Typography>
              </div>
              <div className={classes.column}>
                <img src={project.image} alt='Project' className={classes.file} />
              </div>
              <Typography variant="h6" gutterBottom className={`${classes.row} ${classes.secondRow}`}>
                Description
              </Typography>
              <div className={classes.rowStart}>
                <div className={classes.column}>
                  <Typography variant="subtitle1" gutterBottom>
                    {project.description}
                  </Typography>
                </div>
              </div>
              <div className={`${classes.row}`}>
                <div className={`${classes.row}`}>
                  <Goals goals={project.goals} totalProjectGoal={totalProjectGoal} />
                  <div className={`${classes.column} ${classes.vouchersColumn}`}>
                    <Typography variant="h6" gutterBottom className={`${classes.row} ${classes.secondRow}`}>
                      Vouchers
                    </Typography>
                    {project.vouchers &&
                      project.vouchers.map((voucher, index) => {
                        const onBuyClicked = () => {
                          const newProject = {
                            ...project,
                            goals: [
                              ...project.goals
                            ]
                          };

                          setProject(newProject);
                        };

                        return (
                          <Card
                            clickable={false}
                            projectId={project.id}
                            key={project.id + index}
                            name={voucher.name || 'Project'}
                            creationDate={creationDate}
                            category={project.category || 'Other'}
                            description={voucher.description || 'Description'}
                            additionalPrice={voucher.additionalPrice}
                            originalPrice={voucher.originalPrice}
                            voucherPrice={voucher.voucherPrice}
                            isOwner={isOwner}
                            onBuyClicked={onBuyClicked}
                          />
                        );
                      })
                    }
                    {
                      isOwner && (
                        <div className={classes.centerRow}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            className={classes.addVoucherButton}
                            onClick={onAddVoucherButtonClicked}
                          >
                            Add Voucher
                          </Button>
                        </div>
                      )
                    }
                  </div>
                  {
                    vouchers.map((_, index) => generateVoucherElement(index))
                  }
                </div>
              </div>
            </div>
          </Paper>
        </div>
      }
    </>
  );
};

export default memo(Project);
